<script setup lang="ts">
import { storeToRefs } from 'pinia';

import { ArboButton as Button, ArboIcon as Icon } from '@arbocollab/arbo-ui-components';
import { ref } from 'vue';
import { useNotificationStore } from '@/composables/useNotificationStore';
const notificationStore = useNotificationStore();
const { notifications } = storeToRefs(notificationStore);

const isSvgString = (str: string) => {
    return str.trim().startsWith('<svg');
};

const tooltip = ref('Copy');
const resetTooltip = () => {
    setTimeout(() => {
        tooltip.value = 'Copy';
    }, 3000);
};

const handleCopyError = (message: string) => {
    navigator.clipboard.writeText(message).then(
        () => {
            tooltip.value = 'Copied';
            resetTooltip();
        },
        () => {
            tooltip.value = 'Copy failed';
            resetTooltip();
        }
    );
};

// Add hover state to prevent auto-hide
const hoveredNotifications = ref(new Set<string>());

const onMouseEnter = (id: string) => {
    hoveredNotifications.value.add(id);
    // Assume store has a method to pause auto-dismiss; adjust if needed
    notificationStore.pauseAutoDismiss?.(id);
};

const onMouseLeave = (id: string) => {
    hoveredNotifications.value.delete(id);
    // Assume store has a method to resume auto-dismiss; adjust if needed
    notificationStore.resumeAutoDismiss?.(id);
};
</script>

<template>
    <transition-group name="list" tag="div" class="notification-center" :class="$style['notification-wrapper']">
        <div
            v-for="notification in notifications"
            :key="notification.id"
            v-contrast
            :class="[
                $style['notification-item'],
                $style[`notification--${notification.type || 'info'}`],
                !notification?.actions || notification?.actions?.length == 0 ? $style['snackbar-type'] : ''
            ]"
            @mouseenter="onMouseEnter(notification.id)"
            @mouseleave="onMouseLeave(notification.id)">
            <p :class="$style['snackbar__message']">
                <span :class="$style['snackbar__icon']">
                    <Icon v-if="!notification.icon && notification.type == 'success'" :name="'CircleCheck'" />
                    <Icon v-if="!notification.icon && notification.type == 'interactive'" :name="'CircleCheck'" />
                    <Icon v-else-if="!notification.icon && notification.type == 'error'" :name="'CircleXMark'" />

                    <i
                        v-else-if="notification.icon && isSvgString(notification.icon)"
                        :class="$style['icon-draw']"
                        v-html="notification.icon"></i>
                    <Icon
                        v-else-if="notification.icon && !isSvgString(notification.icon)"
                        :src="notification.icon"
                        color="var(--primary-color)" />
                </span>

                <span :class="$style['error-message']">
                    <span :class="$style['message']" v-html="notification.message"></span>
                </span>
            </p>

            <Button
                v-if="notification.type !== 'interactive'"
                type="secondary"
                :class="$style['copy-btn']"
                class="copy-snackbar-btn"
                :prefix-icon="'Copy'"
                :prefix-tooltip="tooltip"
                :label="''"
                size="24"
                :style="{ minWidth: '24px', width: '24px', height: '24px', padding: '0' }"
                @click="handleCopyError(notification.message)">
            </Button>
        </div>
    </transition-group>
</template>

<style module lang="scss">
.notification-wrapper {
    --text-color: white;
    --icon-color: var(--primary-color);
    position: fixed;
    bottom: 20px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 99;
    display: flex;
    flex-direction: column;
    gap: 8px; // Updated gap
    align-items: center;
}

.notification-item {
    display: flex;
    align-items: center;
    gap: 8px; // Updated gap
    background-color: var(--snackbar-bg, #323232);
    position: relative;
    padding: 4px 8px; // Updated padding
    border-radius: var(--radius-lg); // Updated radius
    box-shadow: var(--card-shadow);
    width: fit-content;
    --max-notification-width: 530px;
    min-width: var(--max-notification-width,);
    max-width: var(--max-notification-width); // Updated max-width
    /* Type-specific styles */
    &.notification--success {
        background-color: var(--snackbar-success-bg);
        color: var(--snackbar-success-text);
        --text-color: var(--snackbar-success-text);
        --icon-color: var(--snackbar-success-text);
    }

    &.notification--error {
        background-color: var(--snackbar-danger-bg);
        color: var(--snackbar-danger-text);
        --text-color: var(--snackbar-danger-text);
        --icon-color: var(--snackbar-danger-text);

        :deep(.close-btn) {
            color: var(--snackbar-danger-text);
        }
    }

    &.notification--info {
        background-color: var(--snackbar-info-bg, #2196f3);
        color: white;

        :deep(.close-btn) {
            color: white;
        }
    }

    &.notification--warning {
        color: white;
        background-color: var(--snackbar-warning-bg, #ff9800);

        :deep(.close-btn) {
            color: white;
        }
    }

    &.notification--interactive {
        background-color: var(--snackbar-interactive-bg, #fff);
        flex-direction: column;
        --max-notification-width: 250px;
        --text-color: var(--text-color);
        --icon-color: var(--snackbar-success-text);
        padding: 8px !important;
    }

    &.snackbar-type {
        min-height: 24px;
        padding: 4px 8px;
        border-radius: 0;
        width: max-content;
        min-width: unset;
        border-radius: var(--radius-lg);
    }
}

.snackbar__message {
    flex-grow: 1;
    display: inline-flex;
    gap: 4px; // Updated gap

    font-weight: 400;
    color: var(--text-color);
    line-height: 16px;
    margin: 0;
    min-height: 24px;
    max-width: 100%;
    width: 100%;
    align-items: center;
    .message {
        word-break: break-word;
        line-height: inherit;

        display: -webkit-box;
        -webkit-line-clamp: 3;
        -webkit-box-orient: vertical;
        overflow: hidden;
    }
    .error-message {
        max-width: calc(var(--max-notification-width) - 80px);
        white-space: wrap;
        line-height: 16px;
    }
}

.snackbar__icon {
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--icon-color);
}
.actions-notification {
    display: flex;
    gap: 4px; // Updated gap
    width: 100%;
    align-items: center;
    justify-content: flex-end;
}
.icon-draw {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
}
.copy-btn {
    height: 24px;
    width: 24px;
    min-width: 24px;
    min-height: 24px;
    padding: 0;
    border-radius: var(--radius-md);

    border: 1px solid var(--button-secondary-color-border-default, #ffffff);
    :deep(.arbo__icon-btn) {
        width: 24px !important;
        height: 24px !important;
    }
}

.close-btn {
    position: absolute;
    top: 4px;
    right: 4px;
    padding: 0 !important;
    height: 24px;
    width: 24px;
    color: #6b6b6b !important;
    &:hover {
        color: var(--primary-color) !important;
    }
}
</style>
<style>
.copy-snackbar-btn .arbo__icon-btn {
    width: 24px !important;
    height: 24px !important;
    padding: 0 !important;
}
</style>
